import React, { Suspense, useCallback } from "react";
import { graphql } from "react-relay";

import { faArrowToBottom } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import uuidv4 from "uuid/v4";

import chromeMeetExtension from "~/legacy-assets/images/extensions/chromeMeetExtension.png";
import firefoxMeetExtension from "~/legacy-assets/images/extensions/firefoxMeetExtension.png";
import { ReactComponent as MeetLogo } from "~/legacy-assets/images/logos/google_meet.svg";
import { useNewOnboardingFlow } from "~/lib/features";
import { useArchitecture } from "~/lib/hooks";
import { useLazyLoadQuery } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import {
	chromeStoreLink,
	desktopDownloadLink,
	isChrome,
	isFirefox,
	isMacos,
	macOsArmDesktopDownloadUri,
	macOsX64DesktopDownloadUri,
	mozillaStoreLink,
	reduceEdgesToNodes,
	withQueryParams,
} from "~/lib/utils";
import { Button, LoadingSpinner } from "~/ui";
import NameList from "~/ui/avatar/NameList";
import type { ButtonProps } from "~/ui/button";

import { ReactComponent as CirclePattern } from "./css/circle-pattern.svg";
import { ReactComponent as WavePattern } from "./css/wave-pattern.svg";
import { ReactComponent as ArrowToDownloadsNoNumber } from "./images/arrow-no-number.svg";
import { ReactComponent as FellowAppImageMac } from "./images/fellow-app-image-mac.svg";
import { ReactComponent as FellowAppImageWindows } from "./images/fellow-app-image-windows.svg";
import { JourneyContext } from "./JourneyContext";

import type { IntegratePageQuery as QueryType } from "~/__generated__/IntegratePageQuery.graphql";
import type {
	IntegratePageTypeQuery,
	IntegratePageTypeQuery$data,
} from "~/__generated__/IntegratePageTypeQuery.graphql";

import styles from "./css/IntegratePage.module.css";
import stylesInterestPage from "./css/InterestPage.module.css";
import stylesOnboarding from "./css/Onboarding.module.css";

export const INTEGRATE_PATH = "/o/integrate/";

type GreekType = NonNullable<IntegratePageTypeQuery$data["user"]["internalAttributes"]>["greekType"];
type ViewProps = {
	userListText: JSX.Element | null;
	showSubtext: boolean;
	greekType: GreekType | undefined;
	onSkip: () => void;
	onComplete: (val?: string) => void;
};

function DownloadButton({
	text,
	url,
	setShowDownloadArrow,
	onComplete,
	...rest
}: {
	text: string;
	url: string;
	onComplete: (val?: string) => void;
	setShowDownloadArrow: (val: boolean) => void;
} & ButtonProps) {
	const [loading, setLoading] = React.useState(false);

	const variant = rest.variant || "primary";

	const onCompleteCallback = useCallback(() => {
		onComplete("desktop-downloaded");
	}, [onComplete]);

	const onNextStep = useCallback(() => {
		dispatchAnalyticsTrack("Onboarding Integration Clicked", {
			...analyticsProperties,
			appExtension: "desktop",
		});
		setTimeout(onCompleteCallback, 1500);
	}, [onCompleteCallback]);

	const onDownloadClick = useCallback(() => {
		onNextStep();
		setShowDownloadArrow(true);
		setLoading(true);
	}, [setShowDownloadArrow, onNextStep]);

	return loading ? (
		<LoadingSpinner inline dimmer={false} />
	) : (
		/* eslint-disable-next-line camelcase */
		<a href={loading ? "" : withQueryParams(url, { utm_content: "onboarding_integrate_page" })} download>
			<Button {...rest} variant={variant} className={styles.downloadButton} onClick={onDownloadClick}>
				{text}
				<FontAwesomeIcon icon={faArrowToBottom} className={styles.downloadIcon} />
			</Button>
		</a>
	);
}

function Subitle(props: { userFirstNames: string[] }) {
	// A list of users who are already using the app (for social proof)
	const { userFirstNames } = props;
	const subtitleText = React.useMemo(() => {
		const endText = (
			<>
				already using Fellow to <b>prepare</b>, <b>record</b>, and <b>collaborate</b> on their meetings.
			</>
		);

		let namesToShow;
		switch (userFirstNames.length) {
			case 0:
				return (
					<>
						The only way to <b>prepare</b>, <b>record</b>, and <b>collaborate</b>
						&nbsp;on your meetings.
					</>
				);
			case 1:
				return (
					<span>
						<b>{userFirstNames[0]}</b> is {endText}
					</span>
				);
			case 2:
				return (
					<span>
						<b>{userFirstNames[0]}</b> and <b>{userFirstNames[1]}</b> are {endText}
					</span>
				);
			case 3:
				return (
					<span>
						<b>{userFirstNames[0]}</b>, <b>{userFirstNames[1]}</b> and <b>{userFirstNames[2]}</b> are {endText}
					</span>
				);
			default:
				namesToShow = userFirstNames.slice(0, 2);
				return (
					<span>
						<b>{namesToShow[0]}</b>, <b>{namesToShow[1]}</b> and <b>{userFirstNames.length - 2} others</b> are {endText}
					</span>
				);
		}
	}, [userFirstNames]);

	return <div className={styles.desktopSubtitle}>{subtitleText}</div>;
}

function DesktopView(props: Pick<ViewProps, "onComplete"> & { userFirstNames: string[] }) {
	const [showDownloadArrow, setShowDownloadArrow] = React.useState(false);
	const architecture = useArchitecture();

	const { onComplete, userFirstNames } = props;

	const downloadSection = React.useMemo(() => {
		if (!isMacos)
			return (
				<DownloadButton
					text="Download Fellow"
					url={desktopDownloadLink}
					onComplete={onComplete}
					setShowDownloadArrow={setShowDownloadArrow}
				/>
			);
		if (architecture == "arm")
			return (
				<DownloadButton
					text="Download Fellow"
					url={macOsArmDesktopDownloadUri}
					onComplete={onComplete}
					setShowDownloadArrow={setShowDownloadArrow}
				/>
			);
		if (architecture == "x64")
			return (
				<DownloadButton
					text="Download Fellow"
					url={macOsX64DesktopDownloadUri}
					onComplete={onComplete}
					setShowDownloadArrow={setShowDownloadArrow}
				/>
			);
		return (
			<div className={styles.multiButtonContainer}>
				<DownloadButton
					text="Intel Mac"
					url={macOsX64DesktopDownloadUri}
					onComplete={onComplete}
					setShowDownloadArrow={setShowDownloadArrow}
				/>
				<DownloadButton
					variant="secondary"
					text="Apple Silicon"
					url={macOsArmDesktopDownloadUri}
					onComplete={onComplete}
					setShowDownloadArrow={setShowDownloadArrow}
				/>
			</div>
		);
	}, [architecture, onComplete]);

	return architecture ? (
		<>
			<div className={styles.desktopTitle}>Download and Install Fellow</div>
			<Suspense
				fallback={
					<>
						The only way to <b>prepare</b>, <b>record</b>, and <b>collaborate</b>
						&nbsp;on your meetings.
					</>
				}
			>
				<Subitle userFirstNames={userFirstNames} />
			</Suspense>
			{isMacos ? (
				<FellowAppImageMac className={styles.fellowAppImage} />
			) : (
				<FellowAppImageWindows className={styles.fellowAppImage} />
			)}
			{downloadSection}
			{showDownloadArrow && (
				<div className={styles.arrowToDownloads}>
					<ArrowToDownloadsNoNumber />
				</div>
			)}
		</>
	) : (
		<></>
	);
}

function GoogleMeetView({ userListText, onSkip, onComplete, showSubtext, greekType }: ViewProps) {
	const onDownload = useCallback(() => {
		const url = isFirefox ? mozillaStoreLink : chromeStoreLink;
		/* eslint-disable-next-line camelcase */
		window.open(withQueryParams(url, { utm_content: "onboarding_integrate_page" }), "_blank", "noopener");
		dispatchAnalyticsTrack("Onboarding Integration Clicked", {
			...analyticsProperties,
			appExtension: "meet",
		});
		onComplete("browser-extension-downloaded");
	}, [onComplete]);

	const isTargetUser = greekType === "BETA" || greekType === "DELTA";
	const isNewOnboardingFlow = useNewOnboardingFlow();

	return (
		<>
			<div className={cx(stylesOnboarding.title, styles.title)}>
				<MeetLogo className={styles.integrationIcon} />
				&nbsp; Fellow in Google Meet
			</div>
			<div className={cx(stylesOnboarding.subtitle, styles.subtitle)}>
				{isTargetUser ? (
					<>
						<strong>Your next step:</strong> Add the {isFirefox ? "Firefox" : "Chrome"} extension for the best
						in-meeting experience using Google Meet. {userListText && <>{userListText} already using the extension.</>}
					</>
				) : userListText ? (
					<>
						{userListText} already viewing meeting notes within Google Meet using the&nbsp;
						{isFirefox ? "Firefox" : "Chrome"} extension.
					</>
				) : (
					<>
						Our users love having meeting agendas side-by-side in Google Meet using Fellow&apos;s{" "}
						{isFirefox ? "Firefox" : "Chrome"} extension.
					</>
				)}
			</div>
			<img src={isFirefox ? firefoxMeetExtension : chromeMeetExtension} className={styles.extensionPreview} />
			<div className={stylesOnboarding.footer}>
				<div className={styles.footerActions}>
					{!isNewOnboardingFlow && (
						<Button
							size="wide"
							onClick={onSkip}
							className={cx(styles.footerButton, { [styles.footerButtonWithSubtext]: showSubtext })}
						>
							Not now
						</Button>
					)}
					<Button
						size="wide"
						variant="primary"
						onClick={onDownload}
						className={cx(styles.footerButton, { [styles.footerButtonWithSubtext]: showSubtext })}
					>
						Add extension
					</Button>
				</div>
				<div className={stylesOnboarding.submessage}>
					<div>Adding the extension takes less than 30 seconds. You can remove it at any time.</div>
				</div>
			</div>
		</>
	);
}

const analyticsProperties = {
	feature: "Onboarding",
	fingerprint: uuidv4(),
};

export default function IntegratePage() {
	const { nextStep } = React.useContext(JourneyContext);

	const { viewer, user } = useLazyLoadQuery<IntegratePageTypeQuery>(
		graphql`
			query IntegratePageTypeQuery {
				viewer {
					user {
						id
					}
					integrations {
						google {
							id
						}
					}
				}
				users {
					edges {
						node {
							id
							firstName
						}
					}
				}
				user {
					id
					internalAttributes {
						primaryConferenceCallProvider
						greekType
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "network-only" },
	);

	const { google } = viewer.integrations;
	const { primaryConferenceCallProvider: provider } = user.internalAttributes ?? {};

	const appExtension = React.useMemo(() => {
		if (provider === "HANGOUTS_MEET" && !!google?.id && (isChrome || isFirefox)) return "meet";
		return "desktop";
	}, [provider, google]);

	const useExtension = appExtension === "meet";
	const useDesktop = appExtension === "desktop";

	const { users } = useLazyLoadQuery<QueryType>(
		graphql`
			query IntegratePageQuery($useExtension: Boolean, $useDesktop: Boolean) {
				users(first: 20, hasUsedBrowserExtension: $useExtension, hasUsedDesktopApp: $useDesktop) {
					edges {
						node {
							id
							firstName
							fullName
						}
					}
				}
			}
		`,
		{ useExtension, useDesktop },
		{ fetchPolicy: "network-only" },
	);

	const usersNodes = React.useMemo(
		() => reduceEdgesToNodes(users?.edges).filter(({ id }) => id !== user.id),
		[user, users],
	);

	const userListText =
		usersNodes.length > 0 ? (
			<>
				<b>
					<NameList omitSelf useFirstName selfId={user.id} users={usersNodes} maxLength={3} />
				</b>{" "}
				{usersNodes.length > 1 ? "are" : "is"}
			</>
		) : null;

	const userFirstNames = reduceEdgesToNodes(users?.edges)
		.filter(({ id }) => id !== viewer.user.id)
		.map(node => node.firstName);

	const onComplete = useCallback(
		(val?: string) => {
			nextStep({ source: INTEGRATE_PATH, val: val ?? "" });
		},
		[nextStep],
	);

	const onSkip = useCallback(() => {
		dispatchAnalyticsTrack("Onboarding Integration Skipped", {
			...analyticsProperties,
			appExtension,
		});
		onComplete();
	}, [appExtension, onComplete]);

	const props = {
		userListText,
		onSkip,
		onComplete,
		greekType: user.internalAttributes?.greekType,
		showSubtext: user.internalAttributes?.greekType !== "ALPHA",
	};
	return (
		<>
			<WavePattern className={cx(stylesInterestPage.wavePattern, stylesOnboarding.pattern)} />
			<CirclePattern className={cx(stylesInterestPage.circlePattern, stylesOnboarding.pattern)} />

			{useExtension && <GoogleMeetView {...props} />}
			{useDesktop && <DesktopView {...props} userFirstNames={userFirstNames} />}
		</>
	);
}
